// Iteration 088 -- Sponsor admin offers CRUD + claims inbox (combined page)
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import type {
  SponsorOfferPublic,
  SponsorOfferClaimPublic,
  OfferTarget,
  OfferClaimStatus,
  DiscountType,
} from "@dugads/shared";
import { OFFER_CLAIM_STATUSES } from "@dugads/shared";
import * as api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, Plus, Trash2, Pencil } from "lucide-react";

type Tab = "offers" | "claims";

export default function SponsorOffersPage() {
  const { sponsorId } = useParams<{ sponsorId: string }>();
  const [tab, setTab] = useState<Tab>("offers");
  const [offers, setOffers] = useState<SponsorOfferPublic[]>([]);
  const [claims, setClaims] = useState<SponsorOfferClaimPublic[]>([]);
  const [canCreate, setCanCreate] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<SponsorOfferPublic | "new" | null>(null);

  const load = useCallback(async () => {
    if (!sponsorId) return;
    setLoading(true);
    try {
      const [offersRes, claimsRes] = await Promise.all([
        api.getSponsorOffers(sponsorId),
        api.getSponsorClaims(sponsorId).catch(() => ({ claims: [] as SponsorOfferClaimPublic[] })),
      ]);
      setOffers(offersRes.offers);
      setCanCreate(offersRes._actions.can_create);
      setClaims(claimsRes.claims);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [sponsorId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function remove(offerId: string) {
    if (!confirm("Delete this offer? Existing claims will be preserved.")) return;
    try {
      await api.deleteSponsorOffer(offerId);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to delete");
    }
  }

  if (loading) return <p className="p-6" data-page="sponsor-offers" data-state="loading">Loading…</p>;

  return (
    <div className="flex flex-col gap-4 p-4" data-page="sponsor-offers">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Sponsor Offers</h1>
        {tab === "offers" && canCreate && (
          <Button onClick={() => setEditing("new")} data-action="new-offer">
            <Plus className="mr-1 h-4 w-4" />
            New offer
          </Button>
        )}
      </div>

      <nav className="flex gap-2 border-b" role="tablist">
        <button
          className={`px-3 py-2 text-sm ${tab === "offers" ? "border-b-2 border-primary font-semibold" : "text-muted-foreground"}`}
          onClick={() => setTab("offers")}
        >
          Offers ({offers.length})
        </button>
        <button
          className={`px-3 py-2 text-sm ${tab === "claims" ? "border-b-2 border-primary font-semibold" : "text-muted-foreground"}`}
          onClick={() => setTab("claims")}
        >
          Claims ({claims.length})
        </button>
      </nav>

      {error && <p className="text-sm text-destructive" data-error>{error}</p>}

      {tab === "offers" &&
        (offers.length === 0 ? (
          <p className="text-sm text-muted-foreground">No offers yet.</p>
        ) : (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
            {offers.map((o) => (
              <Card key={o.id}>
                <CardHeader className="flex flex-row items-start justify-between gap-2">
                  <div>
                    <CardTitle className="text-base">{o.title}</CardTitle>
                    <p className="text-xs text-muted-foreground">
                      {o.target.replace(/_/g, " ")} · {o.active ? "Active" : "Inactive"}
                      {o.discount_value && ` · ${o.discount_value}`}
                    </p>
                  </div>
                  {o._actions.can_edit && (
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => setEditing(o)}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => remove(o.id)}>
                        <Trash2 className="h-3.5 w-3.5 text-destructive" />
                      </Button>
                    </div>
                  )}
                </CardHeader>
                <CardContent>
                  <p className="text-sm line-clamp-3">{o.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        ))}

      {tab === "claims" && <ClaimsInbox claims={claims} onChange={load} />}

      {editing && sponsorId && (
        <OfferEditDialog
          sponsorId={sponsorId}
          offer={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await load();
          }}
        />
      )}
    </div>
  );
}

function ClaimsInbox({
  claims,
  onChange,
}: {
  claims: SponsorOfferClaimPublic[];
  onChange: () => Promise<void>;
}) {
  const [status, setStatus] = useState<"all" | OfferClaimStatus>("all");
  const filtered = useMemo(
    () => claims.filter((c) => status === "all" || c.status === status),
    [claims, status],
  );

  async function update(id: string, next: OfferClaimStatus) {
    try {
      await api.updateClaimStatus(id, next);
      await onChange();
    } catch (e) {
      alert(e instanceof Error ? e.message : "Failed to update status");
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-1">
        <Button
          size="sm"
          variant={status === "all" ? "default" : "outline"}
          onClick={() => setStatus("all")}
        >
          All
        </Button>
        {OFFER_CLAIM_STATUSES.map((s) => (
          <Button
            key={s}
            size="sm"
            variant={status === s ? "default" : "outline"}
            onClick={() => setStatus(s)}
          >
            {s}
          </Button>
        ))}
      </div>
      {filtered.length === 0 ? (
        <p className="text-sm text-muted-foreground">No claims match.</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Submitted</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Preferred time</TableHead>
              <TableHead>Notes</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((c) => (
              <TableRow key={c.id}>
                <TableCell className="text-xs text-muted-foreground">
                  {new Date(c.submitted_at).toLocaleString()}
                </TableCell>
                <TableCell>
                  <div className="font-medium">{c.contact_name}</div>
                  <div className="text-xs text-muted-foreground">
                    <a href={`mailto:${c.contact_email}`} className="hover:underline">
                      {c.contact_email}
                    </a>
                    {c.contact_phone && ` · ${c.contact_phone}`}
                  </div>
                </TableCell>
                <TableCell className="text-xs">{c.preferred_contact_time ?? "--"}</TableCell>
                <TableCell className="max-w-xs truncate text-xs">{c.notes ?? "--"}</TableCell>
                <TableCell>
                  <select
                    title="Status"
                    className="h-8 rounded border px-2 text-xs"
                    value={c.status}
                    onChange={(e) => update(c.id, e.target.value as OfferClaimStatus)}
                  >
                    {OFFER_CLAIM_STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

function OfferEditDialog({
  sponsorId,
  offer,
  onClose,
  onSaved,
}: {
  sponsorId: string;
  offer: SponsorOfferPublic | null;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}) {
  const [title, setTitle] = useState(offer?.title ?? "");
  const [description, setDescription] = useState(offer?.description ?? "");
  const [discountType, setDiscountType] = useState<DiscountType>(offer?.discount_type ?? "freeform");
  const [discountValue, setDiscountValue] = useState(offer?.discount_value ?? "");
  const [offerCode, setOfferCode] = useState(offer?.offer_code ?? "");
  const [target, setTarget] = useState<OfferTarget>(offer?.target ?? "school_and_public");
  const [active, setActive] = useState(offer?.active ?? true);
  const [cta, setCta] = useState(offer?.call_to_action_label ?? "Claim Offer");
  const [externalUrl, setExternalUrl] = useState(offer?.external_url ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    if (!title.trim() || !description.trim()) {
      setError("Title and description are required");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        title: title.trim(),
        description: description.trim(),
        discount_type: discountType,
        discount_value: discountValue.trim() || null,
        offer_code: offerCode.trim() || null,
        target,
        active,
        call_to_action_label: cta.trim() || "Claim Offer",
        external_url: externalUrl.trim() || null,
      };
      if (offer) {
        await api.updateSponsorOffer(offer.id, payload);
      } else {
        await api.createSponsorOffer(sponsorId, payload);
      }
      await onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to save offer");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 p-4 overflow-y-auto">
      <Card className="mt-8 w-full max-w-xl">
        <CardHeader>
          <CardTitle className="text-base">{offer ? "Edit offer" : "New offer"}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="offer-title">Title</Label>
            <Input id="offer-title" value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="offer-desc">Description (markdown)</Label>
            <textarea
              id="offer-desc"
              title="Description"
              placeholder="Short description of the offer"
              className="w-full rounded-md border px-3 py-2 text-sm"
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="offer-discount-type">Discount type</Label>
              <select
                id="offer-discount-type"
                title="Discount type"
                className="flex h-9 w-full rounded-md border px-3 py-1 text-sm"
                value={discountType}
                onChange={(e) => setDiscountType(e.target.value as DiscountType)}
              >
                <option value="percentage">Percentage</option>
                <option value="fixed">Fixed amount</option>
                <option value="freeform">Freeform</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="offer-discount-value">Discount value</Label>
              <Input
                id="offer-discount-value"
                placeholder="20% / R500 / Call for pricing"
                value={discountValue}
                onChange={(e) => setDiscountValue(e.target.value)}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="offer-code">Offer code (optional)</Label>
              <Input id="offer-code" value={offerCode} onChange={(e) => setOfferCode(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="offer-target">Audience</Label>
              <select
                id="offer-target"
                title="Audience"
                className="flex h-9 w-full rounded-md border px-3 py-1 text-sm"
                value={target}
                onChange={(e) => setTarget(e.target.value as OfferTarget)}
              >
                <option value="school">School only</option>
                <option value="school_and_public">School + public</option>
                <option value="public_only">Public only</option>
              </select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="offer-cta">Call-to-action label</Label>
            <Input id="offer-cta" value={cta} onChange={(e) => setCta(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="offer-url">External URL (optional)</Label>
            <Input
              id="offer-url"
              placeholder="https://example.com/promo"
              value={externalUrl}
              onChange={(e) => setExternalUrl(e.target.value)}
            />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={active}
              onChange={(e) => setActive(e.target.checked)}
            />
            Active
          </label>

          {error && <p className="text-sm text-destructive" data-error>{error}</p>}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={save} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving…
                </>
              ) : (
                "Save"
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
